import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetProjectResponse } from '@/api/generated/schemas';
import { RESPONSIBILITY_ROLE_FIELDS } from '@/modules/products/venua-planner/responsibleRoles';

/**
 * UI hodnota destinace ve formuláři – drží bohatý objekt kvůli renderu chipu, ne jen id.
 * Do API příkazu se transformuje na `destinationId` (viz `toProjectRequest`).
 */
const destinationOptionSchema = z.object({
  destinationId: z.number(),
  name: z.string(),
  code: z.string().nullish(),
});

/** UI hodnota osoby ve formuláři. `partyId` = Party.Id. */
const personOptionSchema = z.object({
  partyId: z.number(),
  fullName: z.string(),
});

export type DestinationOption = z.infer<typeof destinationOptionSchema>;
export type PersonOption = z.infer<typeof personOptionSchema>;

/**
 * Factory pro Zod schéma formuláře projektu. Lokalizované hlášky se vkládají přímo do schématu
 * (single source of truth), proto se schéma staví z `t` až za běhu.
 */
export function buildProjectFormSchema(t: TFunction) {
  return z.object({
    name: z.string().trim().min(1, t('project.validation.nameRequired')),
    destinations: z
      .array(destinationOptionSchema)
      .min(1, t('project.validation.destinationsRequired')),
    responsiblePersons: z.object({
      productManager: personOptionSchema.nullable(),
      yieldManager: personOptionSchema.nullable(),
      tourLeader: personOptionSchema.nullable(),
      tourGuide: personOptionSchema.nullable(),
    }),
  });
}

export type ProjectFormValues = z.infer<ReturnType<typeof buildProjectFormSchema>>;

/** Prázdné výchozí hodnoty pro režim zakládání. */
export function emptyProjectFormValues(): ProjectFormValues {
  return {
    name: '',
    destinations: [],
    responsiblePersons: {
      productManager: null,
      yieldManager: null,
      tourLeader: null,
      tourGuide: null,
    },
  };
}

/** Mapuje detail projektu z API na hodnoty formuláře (režim editace). */
export function fromGetProjectResponse(response: GetProjectResponse): ProjectFormValues {
  const values = emptyProjectFormValues();
  values.name = response.name;
  values.destinations = response.destinations.map((destination) => ({
    destinationId: destination.destinationId,
    name: destination.name,
    code: destination.code,
  }));

  for (const responsiblePerson of response.responsiblePersons) {
    const field = RESPONSIBILITY_ROLE_FIELDS.find((item) => item.role === responsiblePerson.role);
    if (field && responsiblePerson.personPartyId != null) {
      values.responsiblePersons[field.key] = {
        partyId: responsiblePerson.personPartyId,
        fullName: responsiblePerson.personFullName ?? `#${responsiblePerson.personPartyId}`,
      };
    }
  }

  return values;
}
